import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Label } from 'designSystem/components/Label'
import { Text } from 'designSystem/components/Text'
import { Textarea } from 'designSystem/components/Textarea'
import { PageMetadataEvalCommentItem } from 'features/knowledgeDocumentViewer/components/metadata/PageMetadataEvalCommentItem'
import type { DocumentPageMetadataUserEditComment } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { formatPageMetadataEvalCommentByline } from 'features/knowledgeDocumentViewer/utils/formatPageMetadataEvalCommentMeta'
import React, { useCallback } from 'react'

const COMMENT_FIELD_ID = 'page-metadata-eval-comment'

export type PageMetadataEvalCommentsProps = {
  comments: readonly DocumentPageMetadataUserEditComment[]
  commentText: string
  canAddComment: boolean
  disabled: boolean
  onCommentTextChange: (value: string) => void
  onAddComment: () => void
}

function EvalComment({
  comment,
}: {
  comment: DocumentPageMetadataUserEditComment
}): React.ReactElement {
  return (
    <PageMetadataEvalCommentItem
      text={comment.text}
      byline={formatPageMetadataEvalCommentByline(
        comment.created_by,
        comment.created_at
      )}
    />
  )
}

export function PageMetadataEvalComments({
  comments,
  commentText,
  canAddComment,
  disabled,
  onCommentTextChange,
  onAddComment,
}: PageMetadataEvalCommentsProps): React.ReactElement {
  const { t } = useLingui()
  const canSubmitComment = canAddComment && commentText.trim().length > 0
  const handleCommentChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onCommentTextChange(event.currentTarget.value)
    },
    [onCommentTextChange]
  )

  return (
    <Box display='flex' direction='column' gap='sm'>
      {comments.length > 0 ? (
        <Box display='flex' direction='column' gap='sm'>
          {comments.map((comment) => (
            <EvalComment
              key={`${comment.created_at}:${comment.created_by}:${comment.text}`}
              comment={comment}
            />
          ))}
        </Box>
      ) : null}
      <Label htmlFor={COMMENT_FIELD_ID}>{t`Comment`}</Label>
      <Textarea
        id={COMMENT_FIELD_ID}
        value={commentText}
        disabled={disabled}
        onChange={handleCommentChange}
      />
      {canAddComment ? (
        <Button
          type='button'
          variant='inputRaised'
          disabled={disabled || !canSubmitComment}
          aria-label={t`Add comment`}
          onClick={onAddComment}>
          {t`Add comment`}
        </Button>
      ) : (
        <Text variant='body' color='muted'>
          {t`Comment is saved when you Confirm or Defer.`}
        </Text>
      )}
    </Box>
  )
}
